import { Injectable } from '@angular/core';
import {
	BehaviorSubject,
	combineLatest,
	debounceTime,
	distinctUntilChanged,
	interval,
	map,
	shareReplay,
	startWith,
	switchMap,
	tap,
} from 'rxjs';

// --- Services ---
import {
	FavoritesService,
	type IGetFavoritesParams,
} from '@services/favorites.service';

// --- Stores ---
import { FavoritesStore } from '@stores/favorites.store';

// --- Utils ---
import { simpleDiff } from '@utils/simple-diff.util';

// --- Interfaces ---
import type { IProduct } from '@interfaces/product';

export const INITIAL_FILTER_STATE: IGetFavoritesParams = {
	page: 1,
};

const REFRESH_INTERVAL = 120 * 1000; // 1 min 30sec

@Injectable({
	providedIn: 'root',
})
export class FavoritesFacade {
	private readonly autoRefresh$ = interval(REFRESH_INTERVAL).pipe(startWith(0));
	private readonly _refresh = new BehaviorSubject(undefined);
	readonly refresh$ = this._refresh.asObservable();

	private readonly _filter = new BehaviorSubject<IGetFavoritesParams>(
		INITIAL_FILTER_STATE
	);
	readonly filter$ = this._filter.asObservable().pipe(
		debounceTime(300),
		distinctUntilChanged((prev, curr) => !simpleDiff(prev, curr))
	);

	private readonly handleRequest$ = combineLatest([
		this.filter$,
		this.autoRefresh$,
		this.refresh$,
	]).pipe(switchMap(([filter]) => this.getFavorites(filter)));

	readonly favoritesState$ = combineLatest([
		this.favoritesStore.favoritesState$,
		this.handleRequest$,
	]).pipe(
		map(([state]) => state),
		shareReplay({ refCount: true })
	);

	constructor(
		private favoritesService: FavoritesService,
		private favoritesStore: FavoritesStore
	) {}

	getFavorites(queryParams?: IGetFavoritesParams, ignoreCache = false) {
		return this.favoritesService.getFavorites(queryParams).pipe(
			tap(favorites => {
				if (ignoreCache) return;
				this.favoritesStore.updateProducts(favorites);
			})
		);
	}

	favoriteProducts(productIds: IProduct['id'][]) {
		return this.favoritesService
			.favoriteProducts(productIds)
			.pipe(tap(favorites => this.favoritesStore.updateProducts(favorites)));
	}

	unfavoriteProducts(productIds: IProduct['id'][]) {
		return this.favoritesService
			.unfavoriteProducts(productIds)
			.pipe(tap(favorites => this.favoritesStore.updateProducts(favorites)));
	}

	filterFavorites(filter: IGetFavoritesParams = {}) {
		const isFilterEmpty = Object.keys(filter).length === 0;
		if (isFilterEmpty) this._filter.next(INITIAL_FILTER_STATE);

		this._filter.next({ ...this._filter.value, ...filter });
	}
}
